import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useSite, resolveImg } from "../context/SiteContext";
import { submitBooking } from "../lib/api";

export default function Booking() {
  const { id } = useParams();
  const nav = useNavigate();
  const { packages } = useSite();
  const pkg = packages.find((p) => p.id === id) || packages[0];

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    travelers: 2,
    start_date: "",
    notes: "",
  });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agree) {
      toast.error("Please accept the terms to continue.");
      return;
    }
    setLoading(true);
    try {
      const booking = await submitBooking({
        package_id: pkg.id,
        package_title: pkg.title,
        package_price: pkg.price,
        ...form,
        travelers: Number(form.travelers),
      });
      toast.success("Booking received! Reference #" + booking.id.slice(0, 8).toUpperCase());
      setTimeout(() => nav("/"), 1500);
    } catch (e) {
      toast.error("Couldn't create booking. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="booking-page" className="pt-32 pb-24 max-w-6xl mx-auto px-6 md:px-12">
      <Link to="/packages" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to packages
      </Link>
      <h1 className="font-display text-5xl md:text-6xl font-light mb-2">Confirm your trip</h1>
      <p className="text-muted-foreground mb-10">A few details and we&apos;ll reserve your slots.</p>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Summary */}
        <aside className="lg:col-span-2">
          <div className="rounded-3xl border border-border overflow-hidden sticky top-28">
            <img src={resolveImg(pkg.cover)} alt={pkg.title} className="h-48 w-full object-cover" />
            <div className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{pkg.days}D · {pkg.nights}N</div>
              <h2 className="font-display text-2xl mb-2">{pkg.title}</h2>
              <p className="text-sm text-muted-foreground mb-4">{(pkg.places || []).join(" · ")}</p>
              <ul className="text-sm space-y-2 mb-5">
                {(pkg.inclusions || []).map((i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {i}</li>
                ))}
              </ul>
              <div className="border-t border-border pt-4 flex items-end justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Per person, from</div>
                  <div className="font-display text-3xl">₹{pkg.price.toLocaleString("en-IN")}</div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={onSubmit} className="lg:col-span-3 rounded-3xl border border-border p-6 md:p-8 bg-card grid sm:grid-cols-2 gap-4" data-testid="booking-form">
          <h3 className="sm:col-span-2 font-display text-2xl mb-2">Traveller details</h3>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="fn">Full name</Label>
            <Input id="fn" required value={form.full_name} onChange={set("full_name")} data-testid="book-name" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="em">Email</Label>
            <Input id="em" type="email" required value={form.email} onChange={set("email")} data-testid="book-email" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ph">Phone</Label>
            <Input id="ph" required value={form.phone} onChange={set("phone")} data-testid="book-phone" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="tv">Travelers</Label>
            <Input id="tv" type="number" min={1} required value={form.travelers} onChange={set("travelers")} data-testid="book-travelers" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sd">Start date</Label>
            <Input id="sd" type="date" required value={form.start_date} onChange={set("start_date")} data-testid="book-date" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="nt">Notes</Label>
            <Textarea id="nt" rows={3} value={form.notes} onChange={set("notes")} data-testid="book-notes" placeholder="Any requests we should know about?" />
          </div>

          <div className="sm:col-span-2 rounded-2xl bg-muted/40 border border-border p-4 text-sm text-muted-foreground">
            <strong className="text-foreground">Payment</strong> — secure online payment will be triggered after we confirm availability. This booking is non-binding until confirmation.
          </div>

          <label className="sm:col-span-2 flex items-start gap-3 text-sm">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="book-terms" />
            <span className="text-muted-foreground">I accept the <Link to="/terms" className="underline">terms</Link> and <Link to="/privacy" className="underline">privacy policy</Link>.</span>
          </label>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={loading} className="rounded-full h-12 w-full" data-testid="book-submit">
              {loading ? "Confirming…" : "Confirm Booking"}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
